import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchBatchDetail,
  grantEnrollment,
  revokeEnrollment,
  transitionBatchStatus,
} from "./adminCourseService";
import { fetchAllUsersWithPagination } from "../services/adminUserService";
import "./adminCourses.css";
import AdminMeetingsTab from "./AdminMeetingsTab";
import AdminAttendanceTab from "./AdminAttendanceTab";

const STATUS_BADGE = {
  DRAFT:           "ac-badge-gray",
  OPEN:            "ac-badge-green",
  FULL:            "ac-badge-orange",
  IN_PROGRESS:     "ac-badge-blue",
  COMPLETED:       "ac-badge-purple",
  CANCELLED:       "ac-badge-red",
};

const ENROLL_STATUS_BADGE = {
  PENDING_PAYMENT: "ac-badge-gray",
  ACTIVE:          "ac-badge-green",
  COMPLETED:       "ac-badge-blue",
  CANCELLED:       "ac-badge-red",
};

const TRANSITIONS = {
  DRAFT:       [{ to: "OPEN",        label: "Open for Enrollment", cls: "ac-btn-success" }],
  OPEN:        [{ to: "IN_PROGRESS", label: "Start Course",        cls: "ac-btn-primary" },
                { to: "CANCELLED",   label: "Cancel Batch",        cls: "ac-btn-danger"  }],
  FULL:        [{ to: "IN_PROGRESS", label: "Start Course",        cls: "ac-btn-primary" },
                { to: "CANCELLED",   label: "Cancel Batch",        cls: "ac-btn-danger"  }],
  IN_PROGRESS: [{ to: "COMPLETED",   label: "Mark Completed",      cls: "ac-btn-success" },
                { to: "CANCELLED",   label: "Cancel Batch",        cls: "ac-btn-danger"  }],
  COMPLETED:   [],
  CANCELLED:   [],
};

const MODE_LABEL = { BATCH: "Group", PERSONAL: "Personal" };

/* ── small progress bar ── */
const MiniProgress = ({ value, max }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
      <div style={{ flex: 1, height: 6, background: "#e5e7eb", borderRadius: 3 }}>
        <div style={{ width: `${pct}%`, height: "100%", background: "#2563eb", borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: "0.78rem", color: "#6b7280", whiteSpace: "nowrap" }}>
        Day {value}/{max}
      </span>
    </div>
  );
};

/* ── grant access modal ── */
const GrantModal = ({ onGrant, onClose, loading }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const handleSearch = (val) => {
    setQuery(val);
    setSelected(null);
    clearTimeout(debounceRef.current);
    if (!val.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetchAllUsersWithPagination({ search: val, page: 0, size: 8 });
        setResults(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }}>
      <div style={{
        background: "#fff", borderRadius: 10, padding: "1.5rem",
        width: "100%", maxWidth: 400, boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
      }}>
        <h3 style={{ margin: "0 0 1rem", fontSize: "1.1rem" }}>Grant Access</h3>
        <div className="ac-form-group">
          <label>Search User</label>
          <input
            type="text"
            value={selected ? `${selected.name} (${selected.email})` : query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name or email…"
            autoFocus
            onFocus={() => { if (selected) { setSelected(null); setResults([]); setQuery(""); } }}
          />
        </div>

        {/* dropdown results */}
        {!selected && (results.length > 0 || searching) && (
          <div style={{
            border: "1px solid #e5e7eb", borderRadius: 6, marginTop: -8,
            marginBottom: "1rem", maxHeight: 200, overflowY: "auto",
          }}>
            {searching && (
              <div style={{ padding: "0.6rem 0.9rem", fontSize: "0.82rem", color: "#9ca3af" }}>Searching…</div>
            )}
            {results.map((u) => (
              <div
                key={u.userId}
                onClick={() => { setSelected(u); setResults([]); }}
                style={{
                  padding: "0.55rem 0.9rem", cursor: "pointer",
                  borderBottom: "1px solid #f3f4f6", fontSize: "0.88rem",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f0f9ff"}
                onMouseLeave={(e) => e.currentTarget.style.background = ""}
              >
                <span style={{ fontWeight: 600 }}>{u.name}</span>
                <span style={{ color: "#6b7280", marginLeft: 6 }}>{u.email}</span>
              </div>
            ))}
          </div>
        )}

        {selected && (
          <div style={{
            background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6,
            padding: "0.5rem 0.9rem", marginBottom: "1rem", fontSize: "0.88rem",
          }}>
            ✓ <strong>{selected.name}</strong> — {selected.email}
          </div>
        )}

        <p style={{ fontSize: "0.82rem", color: "#6b7280", margin: "0 0 1rem" }}>
          This will enroll the user without payment (admin-granted).
        </p>
        <div className="ac-actions">
          <button
            className="ac-btn ac-btn-success"
            disabled={!selected || loading}
            onClick={() => onGrant(selected.id)}
          >
            {loading ? "Granting…" : "Grant Access"}
          </button>
          <button className="ac-btn ac-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
const AdminBatchDetailPage = () => {
  const { id: courseId, batchId } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("students");
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [granting, setGranting] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchBatchDetail(courseId, batchId);
      setBatch(res.data);
    } catch {
      setError("Failed to load batch.");
    } finally {
      setLoading(false);
    }
  }, [courseId, batchId]);

  useEffect(() => { load(); }, [load]);

  const handleTransition = async (to) => {
    if (!window.confirm(`Move batch to ${to}?`)) return;
    setTransitioning(true);
    setError("");
    try {
      await transitionBatchStatus(courseId, batchId, to);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Transition failed.";
      setError(String(msg));
    } finally {
      setTransitioning(false);
    }
  };

  const handleGrant = async (userId) => {
    setGranting(true);
    setError("");
    try {
      await grantEnrollment(courseId, batchId, userId);
      setShowGrantModal(false);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Grant failed.";
      setError(String(msg));
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (enrollment) => {
    if (!window.confirm(`Revoke enrollment for ${enrollment.userName}?`)) return;
    setError("");
    try {
      await revokeEnrollment(courseId, batchId, enrollment.enrollmentId);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Revoke failed.";
      setError(String(msg));
    }
  };

  if (loading) return <div className="ac-loading">Loading batch…</div>;
  if (!batch) return <div className="ac-empty">Batch not found.</div>;

  const transitions = TRANSITIONS[batch.status] || [];
  const students = batch.enrolledStudents || [];

  return (
    <div>
      {/* ── header ── */}
      <div className="ac-header">
        <div>
          <h2>{batch.batchName}</h2>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.3rem", flexWrap: "wrap" }}>
            <span className={`ac-badge ${STATUS_BADGE[batch.status] || "ac-badge-gray"}`}>
              {batch.status}
            </span>
            <span className="ac-badge ac-badge-blue">
              {MODE_LABEL[batch.mode] || batch.mode}
            </span>
            <span style={{ fontSize: "0.82rem", color: "#6b7280" }}>
              {batch.courseName || ""}
            </span>
          </div>
        </div>
        <div className="ac-actions">
          {transitions.map(({ to, label, cls }) => (
            <button
              key={to}
              className={`ac-btn ${cls} ac-btn-sm`}
              disabled={transitioning}
              onClick={() => handleTransition(to)}
            >
              {label}
            </button>
          ))}
          <button
            className="ac-btn ac-btn-ghost ac-btn-sm"
            onClick={() => navigate(`/admin/courses/${courseId}/batches`)}
          >
            ← Batches
          </button>
        </div>
      </div>

      {/* ── meta info ── */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
        gap: "0.75rem", marginBottom: "1.5rem",
      }}>
        {[
          ["Start Date",    batch.startDate || "—"],
          ["Daily Time",    batch.dailySessionTime ? `${batch.dailySessionTime} ${batch.timezone || ""}` : "—"],
          ["Instructor",    batch.instructorName || "—"],
          ["Capacity",      batch.mode === "PERSONAL" ? "1" : `${batch.minCapacity ?? "—"} – ${batch.maxCapacity ?? "—"}`],
          ["Enrolled",      students.length],
        ].map(([label, val]) => (
          <div key={label} style={{
            background: "#fff", borderRadius: 8, padding: "0.75rem 1rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
          }}>
            <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.2rem" }}>{label}</div>
            <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>{val}</div>
          </div>
        ))}
      </div>

      {error && (
        <div style={{
          background: "#fee2e2", color: "#b91c1c",
          padding: "0.6rem 0.9rem", borderRadius: "6px",
          marginBottom: "1rem", fontSize: "0.88rem",
        }}>
          {error}
        </div>
      )}

      {/* ── tabs ── */}
      <div style={{ display: "flex", gap: "0.25rem", marginBottom: "1rem", borderBottom: "2px solid #e5e7eb" }}>
        {["students", "meetings", "attendance", "assessments"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "0.5rem 1.1rem",
              border: "none",
              background: "none",
              cursor: "pointer",
              fontWeight: activeTab === tab ? 700 : 400,
              color: activeTab === tab ? "#2563eb" : "#6b7280",
              borderBottom: activeTab === tab ? "2px solid #2563eb" : "2px solid transparent",
              marginBottom: "-2px",
              fontSize: "0.9rem",
              textTransform: "capitalize",
            }}
          >
            {tab}
            {tab === "students" && students.length > 0 && (
              <span style={{
                marginLeft: "0.4rem", background: "#dbeafe", color: "#1d4ed8",
                borderRadius: "999px", padding: "0.1rem 0.45rem", fontSize: "0.72rem",
              }}>
                {students.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── students tab ── */}
      {activeTab === "students" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
            <button
              className="ac-btn ac-btn-success"
              onClick={() => setShowGrantModal(true)}
            >
              + Grant Access
            </button>
          </div>

          {students.length === 0 ? (
            <div className="ac-empty">No students enrolled yet.</div>
          ) : (
            <div className="ac-table-wrap">
              <table className="ac-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Progress</th>
                    <th>Enrolled At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.enrollmentId}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                          {s.userName}
                          {s.adminGranted && (
                            <span className="ac-badge ac-badge-orange" style={{ fontSize: "0.68rem" }}>
                              Admin
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: "0.85rem", color: "#6b7280" }}>{s.userEmail}</td>
                      <td>
                        <span className={`ac-badge ${ENROLL_STATUS_BADGE[s.status] || "ac-badge-gray"}`}>
                          {s.status}
                        </span>
                      </td>
                      <td style={{ minWidth: 140 }}>
                        <MiniProgress value={s.currentDay || 0} max={batch.totalDays || 21} />
                      </td>
                      <td style={{ fontSize: "0.82rem", color: "#6b7280" }}>
                        {s.enrolledAt ? new Date(s.enrolledAt).toLocaleDateString("en-IN") : "—"}
                      </td>
                      <td>
                        <div className="ac-actions">
                          <button
                            className="ac-btn ac-btn-primary ac-btn-sm"
                            onClick={() =>
                              navigate(`/admin/enrollments/${s.enrollmentId}/assessments`, { state: { courseId } })
                            }
                          >
                            Assess
                          </button>
                          <button
                            className="ac-btn ac-btn-danger ac-btn-sm"
                            onClick={() => handleRevoke(s)}
                          >
                            Revoke
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── assessments tab ── */}
      {activeTab === "assessments" && (
        <div style={{ paddingTop: "0.5rem" }}>
          <p style={{ fontSize: "0.88rem", color: "#6b7280", marginBottom: "1rem" }}>
            View INITIAL and FINAL course assessments for all students in this batch.
          </p>
          <button
            className="ac-btn ac-btn-primary"
            onClick={() => navigate(`/admin/courses/${courseId}/batches/${batchId}/assessments`)}
          >
            Open Batch Assessments →
          </button>
        </div>
      )}

      {/* ── meetings tab ── */}
      {activeTab === "meetings" && (
        <AdminMeetingsTab
          courseId={courseId}
          batchId={batchId}
          mode={batch.mode}
          students={students}
        />
      )}

      {/* ── attendance tab ── */}
      {activeTab === "attendance" && (
        <AdminAttendanceTab
          courseId={courseId}
          batchId={batchId}
          students={students}
        />
      )}

      {/* ── grant modal ── */}
      {showGrantModal && (
        <GrantModal
          onGrant={handleGrant}
          onClose={() => setShowGrantModal(false)}
          loading={granting}
        />
      )}
    </div>
  );
};

export default AdminBatchDetailPage;
